import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ReactNode } from 'react';
import {
  StudioContentEpisodesStatusTabType,
  StudioContentEpisodesTabType,
  StudioContentsTabType,
} from '@diritto/common/content/constants';
import { EpisodeOrderType } from '@diritto/common/episode/constants';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { isPC } from 'constants/layout';
import { Divider } from 'ui-components/common/Divider';
import { Link } from 'ui-components/common/Link';

interface TabProps {
  className?: string;
  leftTabs?: ReactNode;
  rightTabs?: ReactNode;
}

export const Tab = ({ className, leftTabs, rightTabs }: TabProps) => (
  <div className={`${className} flex flex-col w-full relative`}>
    <div className="flex flex-row w-full items-center justify-between px-4">
      {leftTabs}
      {rightTabs}
    </div>
    <StyledDivider color={Color.Gray300} />
  </div>
);

const StyledDivider = styled(Divider)`
  width: 100%;
  bottom: 0;

  @media (${isPC}) {
    left: 0;
  }
`;

export const TabList = styled.ul(
  ({ gap = 24 }: { gap?: number }) => css`
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: ${gap}px;
    overflow: auto;
  `,
);

export const ListItem = styled.li(
  ({ isActive = false }: { isActive?: boolean }) => css`
    padding: 0;
    height: 44px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    text-align: center;

    ${Typography.XSmall}
    color: ${Color.Black};
    font-weight: ${FontWeight.Regular};
    border-bottom: 3px solid transparent;

    ${isActive &&
    css`
      color: ${Color.Primary500};
      font-weight: ${FontWeight.Bold};
      border-bottom: 3px solid ${Color.Primary500};
    `}
  `,
);

export const ContestListItem = styled.li(
  ({ isActive = false }: { isActive?: boolean }) => css`
    height: 55px;
    display: flex;
    justify-content: center;

    flex-direction: row;
    gap: 4px;
    cursor: pointer;
    text-align: center;

    ${isActive &&
    css`
      font-weight: ${FontWeight.Bold};
    `}
  `,
);

export const RightListItem = styled.div(
  ({ isActive = false }: { isActive?: boolean }) => css`
    height: 45px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    cursor: pointer;
    text-align: center;

    color: ${Color.Black};
    font-weight: ${isActive ? FontWeight.Bold : FontWeight.Regular};
    ${Typography.XXSmall};
  `,
);

export const StudioContentDetailTabItem = ({
  contentId,
  selectedTabType,
  tabType,
  episodeOrderType,
  noticeOrderType,
  statusType,
  text,
}: {
  contentId: number;
  selectedTabType: StudioContentEpisodesTabType;
  tabType: StudioContentEpisodesTabType;
  episodeOrderType: EpisodeOrderType;
  noticeOrderType: EpisodeOrderType;
  statusType: StudioContentEpisodesStatusTabType;
  text: string;
}) => (
  <Link
    key={tabType}
    href={`/studio/contents/${contentId}?tabType=${tabType}&episodeOrderType=${episodeOrderType}&noticeOrderType=${noticeOrderType}&statusType=${statusType}`}
    replace
    shallow>
    <ListItem isActive={selectedTabType === tabType}>{text}</ListItem>
  </Link>
);

export const EpisodeOrderTabItem = ({
  contentId,
  selectedOrderType,
  tabType,
  episodeOrderType,
  noticeOrderType,
  statusType,
  text,
}: {
  contentId: number;
  selectedOrderType: EpisodeOrderType;
  tabType: StudioContentEpisodesTabType;
  episodeOrderType: EpisodeOrderType;
  noticeOrderType: EpisodeOrderType;
  statusType: StudioContentEpisodesStatusTabType;
  text: string;
}) => {
  const url = `/studio/contents/${contentId}?tabType=${tabType}&episodeOrderType=${episodeOrderType}&noticeOrderType=${noticeOrderType}&statusType=${statusType}`;
  return (
    <Link key={episodeOrderType} href={url} replace shallow>
      <RightListItem isActive={selectedOrderType === episodeOrderType}>{text}</RightListItem>
    </Link>
  );
};

export const NoticeOrderTabItem = ({
  contentId,
  selectedOrderType,
  tabType,
  episodeOrderType,
  noticeOrderType,
  statusType,
  text,
}: {
  contentId: number;
  selectedOrderType: EpisodeOrderType;
  tabType: StudioContentEpisodesTabType;
  episodeOrderType: EpisodeOrderType;
  noticeOrderType: EpisodeOrderType;
  statusType: StudioContentEpisodesStatusTabType;
  text: string;
}) => {
  const url = `/studio/contents/${contentId}?tabType=${tabType}&episodeOrderType=${episodeOrderType}&noticeOrderType=${noticeOrderType}&statusType=${statusType}`;
  return (
    <Link key={episodeOrderType} href={url} replace shallow>
      <RightListItem isActive={selectedOrderType === noticeOrderType}>{text}</RightListItem>
    </Link>
  );
};

export const EpisodeStatusTabItem = ({
  contentId,
  selectedStatusType,
  tabType,
  episodeOrderType,
  noticeOrderType,
  statusType,
  text,
}: {
  contentId: number;
  selectedStatusType: StudioContentEpisodesStatusTabType;
  tabType: StudioContentEpisodesTabType;
  episodeOrderType: EpisodeOrderType;
  noticeOrderType: EpisodeOrderType;
  statusType: StudioContentEpisodesStatusTabType;
  text: string;
}) => (
  <Link
    key={episodeOrderType}
    href={`/studio/contents/${contentId}?tabType=${tabType}&episodeOrderType=${episodeOrderType}&noticeOrderType=${noticeOrderType}&statusType=${statusType}`}
    replace
    shallow>
    <RightListItem isActive={selectedStatusType === statusType}>{text}</RightListItem>
  </Link>
);

export const StudioContentsTabItem = ({
  selectedTabType,
  tabType,
  text,
}: {
  selectedTabType: StudioContentsTabType;
  tabType: StudioContentsTabType;
  text: string;
}) => (
  <StyledLink>
    <Link key={tabType} href={`/studio/contents?tabType=${tabType}`} replace shallow>
      <ListItem isActive={selectedTabType === tabType}>{text}</ListItem>
    </Link>
  </StyledLink>
);

const StyledLink = styled.div`
  display: flex;
  flex-direction: row;
  white-space: nowrap;
  position: relative;
  justify-content: center;
  align-items: center;
`;

export const StudioContentsContestTabItem = ({
  selectedTabType,
  tabType,
  text,
  count,
  isRedDot,
}: {
  selectedTabType: StudioContentsTabType;
  tabType: StudioContentsTabType;
  text: string;
  count: number;
  isRedDot?: boolean;
}) => (
  <StyledLink>
    <div className="h-[18px] mr-4">
      <svg width="1" height="18" viewBox="0 0 1 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" clipRule="evenodd" d="M0 17.25V0.75H1V17.25H0Z" fill="#D0D7DE" />
      </svg>
    </div>
    <Link key={tabType} href={`/studio/contents?tabType=${tabType}`} replace shallow>
      <ListItem isActive={selectedTabType === tabType}>
        <div className="flex flex-row items-center">
          <p>
            {text} {count ?? '0'}
          </p>
          {isRedDot && (
            <div className="mb-3">
              <div className="w-1 h-1 bg-red-500 rounded-full" />
            </div>
          )}
        </div>
      </ListItem>
    </Link>
  </StyledLink>
);
